import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import DeckGL from "@deck.gl/react";
import type { Layer, ControllerProps } from "@deck.gl/core";
import { Map as MapLibreMap, Source, Layer as MapLayer } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import type { FeatureCollection, LineString, Point, Polygon, MultiPolygon } from "geojson";
import { SIKHIO } from "@sikhio/shared";
import type {
  AcademicSnapshot,
  AirQualityPoint,
  CampusZoneProperties,
  ExecutiveSnapshot,
  IncidentFeature,
  IntelligenceItem,
  WeatherSnapshot,
} from "@sikhio/shared";

import { useFeed } from "./hooks/useFeed";
import { buildTrafficSamples, type RoadProps } from "./sim/trafficSim";
import {
  buildingsLayer,
  campusBoundaryLayer,
  cctvLayer,
  esriSatelliteLayer,
  himawariInfraredLayer,
  openTopoTerrainLayer,
  incidentLayer,
  trafficHeatmapLayer,
  roadNetworkLayer,
  datagoPointsLayer,
  distanceGridLayer,
  distanceGridLabelsLayer,
  civicPointsLayer,
  waterwaysLayer,
  waterFlowArrowsLayer,
  waterBodiesLayer,
  railwayLayer,
  floodRiskLayer,
  templeSpiresLayer,
  oldTownDistrictLayer,
  type HeritageFeatureProps,
  CIVIC_PALETTE,
  type CivicKind,
  type DatagoPoint,
  type BuildingProperties,
  type CctvCamera,
  type ClassifiedRoadProps,
  air4thaiLayer,
  lamTakhongLayer,
  damOutflowLayer,
  upstreamToDamLayer,
  attractionsLayer,
  healthTambonLayer,
  newsPinsLayer,
} from "./map/layers";
import { useTile3DLayer } from "./map/Tile3DLayer";
import { useGoogleTileSession } from "./hooks/useGoogleTileSession";
import { googleTileTemplate } from "./lib/googleTiles";
import { ALL_LAYERS, LENSES, layerCanEnable, type LayerId, type LensId, type MapViewState } from "./map/presets";
import { buildBasemapStyle, type BasemapId } from "./map/basemaps";
import { BasemapStrip } from "./components/BasemapStrip";
import { CollapsibleSection } from "./components/CollapsibleSection";
import { MapCursorReadout } from "./components/MapCursorReadout";
import { BuildingLegend } from "./components/BuildingLegend";

import { TopBar } from "./components/TopBar";
import { HourRail } from "./components/HourRail";
import { LayerPalette, type LayerStatus } from "./components/LayerPalette";
import { LensPicker } from "./components/LensPicker";
import { KpiStrip } from "./components/KpiStrip";
import { PmcuBrief } from "./components/PmcuBrief";
import { SocialListeningPanel } from "./components/SocialListeningPanel";
import { WaterPanel, type ReservoirStatus } from "./components/WaterPanel";
import { ProvincialKPIs, type ProvincialKPIs as ProvincialKPIsType } from "./components/ProvincialKPIs";
import { EarthAlphaBrief } from "./components/EarthAlphaBrief";
import { FloodRiskPanel } from "./components/FloodRiskPanel";
import { FloodDashPanel } from "./components/FloodDashPanel";
import { BasinGaugesStrip } from "./components/BasinGaugesStrip";
import { AirDashPanel } from "./components/AirDashPanel";
import { DroughtPanel } from "./components/DroughtPanel";
import { HealthPanel } from "./components/HealthPanel";
import { MunicipalProfilePanel } from "./components/MunicipalProfilePanel";
import { KhaoYaiPanel, type KhaoYaiStatus } from "./components/KhaoYaiPanel";
import { AgriPanel, type CropPrice } from "./components/AgriPanel";
import { RailPanel, type TrainService } from "./components/RailPanel";
import { WastePanel, type WasteStatus } from "./components/WastePanel";
import { ElderCarePanel, type ElderCareStatus } from "./components/ElderCarePanel";
import { HighwayPanel, type HighwayStatus } from "./components/HighwayPanel";
import { LocalMarketPanel, type LocalMarketData } from "./components/LocalMarketPanel";
// Heavy modals + below-the-fold widgets — lazy-loaded so they're
// excluded from the initial bundle. Each loads only on first open;
// subsequent opens are instant (module cached).
//
// Performance rationale (audit-r2 Batch C):
//   - ChatBox: floating widget, hidden by default. Was ~40KB gz.
//   - PredictivePanel: bottom of left rail, off-screen on phone.
//   - TrendsPanel: top of right rail, below the fold on most viewports.
//   - NewsDesk: middle of right rail, the heaviest right-rail consumer
//     (large data prop, complex virtualised list).
//   - FacebookPanel: bottom of right rail, off-screen on phone.
const SourceCatalog = lazy(() => import("./components/SourceCatalog").then((m) => ({ default: m.SourceCatalog })));
const Manual = lazy(() => import("./components/Manual").then((m) => ({ default: m.Manual })));
const Whitepaper = lazy(() => import("./components/Whitepaper").then((m) => ({ default: m.Whitepaper })));
const SheetsPanel = lazy(() => import("./components/SheetsPanel").then((m) => ({ default: m.SheetsPanel })));
const SituationDigest = lazy(() => import("./components/SituationDigest").then((m) => ({ default: m.SituationDigest })));
const ChatBox = lazy(() => import("./components/ChatBox").then((m) => ({ default: m.ChatBox })));
const PredictivePanelLazy = lazy(() => import("./components/PredictivePanel").then((m) => ({ default: m.PredictivePanel })));
const TrendsPanelLazy = lazy(() => import("./components/TrendsPanel").then((m) => ({ default: m.TrendsPanel })));
const NewsDeskLazy = lazy(() => import("./components/NewsDesk").then((m) => ({ default: m.NewsDesk })));
const FacebookPanelLazy = lazy(() => import("./components/FacebookPanel").then((m) => ({ default: m.FacebookPanel })));

// Inline the SheetsPanel URL check so we don't eagerly load the whole module.
const SHEETS_STORAGE_KEY = "sikhio:sheets-url-v1";
import { AqiBadge, type AqiTrend } from "./components/AqiBadge";
import { BuildingCard } from "./components/BuildingCard";
import { IncidentCard } from "./components/IncidentCard";
import { CctvLiveModal } from "./components/CctvLiveModal";
import { CctvWall } from "./components/CctvWall";
import { CctvCentre } from "./components/CctvCentre";
import { useCvCentres } from "./hooks/useCvCentres";
import { BuildingSearch } from "./components/BuildingSearch";
import { MapOverlayControls } from "./components/MapOverlayControls";
import type { TrendsSnapshot } from "./components/TrendsPanel";
import { useSystemHealth } from "./hooks/useSystemHealth";
import { systemBannerVerdict, bannerMessage } from "./lib/systemBanner";
import { useDeferredReady } from "./hooks/useDeferredReady";
import { MobileNav, type MobilePanel } from "./components/MobileNav";
import { MayorsBrief } from "./components/MayorsBrief";
import { METRIC_LAYER_MAP, METRIC_LABEL } from "./lib/predictiveRegistry";
import type { ForecastMetric } from "./components/PredictivePanel";
import { ExecutiveBriefing } from "./components/ExecutiveBriefing";
import { WaterSecurityHero } from "./components/WaterSecurityHero";
import { API_BASE } from "./lib/apiBase";
import type { NasaEarthReadings, FacebookPost, DamReading, BasinStation, DroughtBrief, FloodDashBrief, AirDashBrief, HealthTambon, Attraction, MunicipalityProfile, FallbackTier } from "@sikhio/shared";
import { useIsMobile } from "./hooks/useMediaQuery";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useTheme } from "./hooks/useTheme";
import { expandSlimBuilding, type SlimBuildingProperties } from "./lib/building";
import { filterBuildingsToViewport, viewportKey } from "./lib/viewportFilter";
import { Arrow } from "./components/Arrow";

/**
 * Basemap — CARTO no-labels at the bottom, labels rendered on TOP via a
 * separate raster source so deck.gl context layers can interleave between
 * them. Theme-aware: dark to carto dark + deep navy ocean tint;
 * light <Arrow direction="right" /> carto positron-light + pale sky-blue ocean tint.
 */
// Yesterday ISO date — GIBS tiles publish with ~24 h delay
const GIBS_DATE = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const GIBS_BASE = "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best";

function gibsUrl(product: string, level: number, format: "jpg" | "png"): string {
  return `${GIBS_BASE}/${product}/default/${GIBS_DATE}/GoogleMapsCompatible_Level${level}/{z}/{y}/{x}.${format}`;
}

// GIBS satellite layers rendered via MapLibre <Source>/<Layer> — MapLibre handles
// raster tile stretching perfectly (proven by the carto basemap).
// Deck.gl TileLayer is kept for esriSatelliteLayer only.
const GIBS_LAYERS: Array<{
  id: string;
  product: string;
  level: number;
  format: "jpg" | "png";
  opacity: number;
}> = [
  { id: "satellite-true-color",         product: "MODIS_Terra_CorrectedReflectance_TrueColor",    level: 9, format: "jpg", opacity: 0.85 },
  { id: "satellite-viirs-truecolor",     product: "VIIRS_NOAA20_CorrectedReflectance_TrueColor",   level: 9, format: "jpg", opacity: 0.85 },
  { id: "satellite-night",               product: "VIIRS_SNPP_DayNightBand_ENCC",                  level: 8, format: "png", opacity: 0.85 },
  { id: "satellite-imerg",               product: "IMERG_Precipitation_Rate",                      level: 6, format: "png", opacity: 0.75 },
  { id: "satellite-ndvi",                product: "MODIS_Terra_NDVI_8Day",                         level: 9, format: "png", opacity: 0.70 },
  { id: "satellite-soil-root",           product: "SMAP_L4_Analyzed_Root_Zone_Soil_Moisture",      level: 6, format: "png", opacity: 0.70 },
  { id: "satellite-soil-surface",        product: "SMAP_L4_Analyzed_Surface_Soil_Moisture",        level: 6, format: "png", opacity: 0.70 },
  { id: "satellite-lst",                 product: "MODIS_Terra_Land_Surface_Temp_Day",             level: 7, format: "png", opacity: 0.70 },
  { id: "satellite-aerosol",             product: "MODIS_Combined_Value_Added_AOD",                level: 7, format: "png", opacity: 0.70 },
  { id: "satellite-no2",                 product: "OMI_Nitrogen_Dioxide_Tropo_Column",             level: 6, format: "png", opacity: 0.70 },
  { id: "satellite-flood",               product: "MODIS_Combined_Flood_3-Day",                    level: 7, format: "png", opacity: 0.75 },
];

// Pre-computed, stable references for the React-rendered <Source>/<Layer> props.
// Without these, `tiles={[gibsUrl(...)]}` and `paint={{ "raster-opacity": ... }}`
// allocate a new array + object on every render, forcing react-map-gl to deep-diff
// raster sources on every map gesture.
const GIBS_RENDER: Array<{
  id: string;
  tiles: readonly [string];
  paint: { readonly "raster-opacity": number };
  maxzoom: number;
}> = GIBS_LAYERS.map((g) => ({
  id: g.id,
  tiles: [gibsUrl(g.product, g.level, g.format)] as const,
  paint: { "raster-opacity": g.opacity } as const,
  maxzoom: g.level,
}));

// GISTDA disaster raster layers — flood extent + drought risk from the
// official Thai disaster platform, streamed through our /api/gistda/tiles
// proxy so the API key stays server-side. NOTE: the upstream path is named
// ".../tms/{z}/{x}/{y}" but it actually serves XYZ orientation (y from the
// top) — verified 2026-09-18: the DRIPlus tile with drought data sits at the
// XYZ y, and the TMS-flipped y is blank. So this uses MapLibre's default XYZ
// scheme, NOT scheme="tms".
const GISTDA_TILE_RENDER: Array<{
  id: LayerId;
  tiles: readonly [string];
  paint: { readonly "raster-opacity": number };
}> = [
  { id: "gistda-flood-7d",   proxy: "flood-7days",  opacity: 0.72 },
  { id: "gistda-flood-30d",  proxy: "flood-30days", opacity: 0.6 },
  { id: "gistda-flood-freq", proxy: "flood-freq",   opacity: 0.5 },
  { id: "gistda-dri",        proxy: "dri",          opacity: 0.62 },
  { id: "gistda-ndwi",       proxy: "ndwi",         opacity: 0.58 },
].map((g) => ({
  id: g.id as LayerId,
  tiles: [`${API_BASE}/api/gistda/tiles/${g.proxy}/{z}/{x}/{y}`] as const,
  paint: { "raster-opacity": g.opacity } as const,
}));


function normalizeProperties<T extends { features?: Array<{ properties?: object | null }> }>(data: T): T {
  if (data && Array.isArray(data.features)) {
    for (const f of data.features) {
      if (f.properties === null || f.properties === undefined) {
        (f as Record<string, unknown>).properties = {};
      }
    }
  }
  return data;
}

function useGeoJson<T extends { features?: Array<{ properties?: object | null }> }>(path: string) {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(path, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        setData(normalizeProperties(j as T));
      })
      .catch(() => {});
    return () => {
      ctrl.abort();
    };
  }, [path]);
  return data;
}


function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const MemoTopBar = memo(TopBar);
const MemoHourRail = memo(HourRail);
const MemoLayerPalette = memo(LayerPalette);
const MemoLensPicker = memo(LensPicker);
const MemoKpiStrip = memo(KpiStrip);
const MemoPmcuBrief = memo(PmcuBrief);
const MemoExecutiveBriefing = memo(ExecutiveBriefing);

export default function App() {
  const { theme } = useTheme();
  const [basemapId, setBasemapId] = useState<BasemapId>("carto");
  const mapStyle = useMemo(() => buildBasemapStyle(basemapId, theme), [basemapId, theme]);
  const { health: systemHealth, error: systemHealthError, refetch: refetchSystemHealth } = useSystemHealth(60_000);
  // Whether the feed-failure banner is warranted (null = stay quiet).
  const bannerVerdict = useMemo(
    () => (systemHealth ? systemBannerVerdict(systemHealth.system, systemHealth.adapters) : null),
    [systemHealth],
  );

  // Defer low-priority 24h-TTL reference feeds by 800ms so they don't
  // compete with the live water / air / dam / drought feeds for the
  // first-paint network budget. The map and the water-security hero
  // get to render first; the reference data (municipality profile,
  // attractions, primary-care) fills in shortly after.
  // 800ms is below the perceptual threshold for "interactive" (100ms)
  // and well below "load" thresholds users actually notice (~2s).
  const referenceReady = useDeferredReady(800);
  // Municipal boundary — Sikhio district perimeter (OSM-derived GeoJSON).
  const campus = useGeoJson<FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>>(
    "/geo/sikhio-district.geojson",
  );
  // Buildings — load the slim on-wire form (42% smaller raw / 23% smaller
  // gzipped than the original OSM export) and expand to the full
  // BuildingProperties once at load time. The expansion is a pure
  // function over 9,900 features — runs in <10ms via useMemo, so
  // there's no per-render cost. The slim file is generated by
  // `node scripts/slim-buildings.mjs`; the OSM upstream is regenerated
  // by `node scripts/fetch-osm-buildings.mjs` (Overpass API).
  const buildingsSlim = useGeoJson<
    FeatureCollection<Polygon | MultiPolygon, SlimBuildingProperties>
  >("/geo/sikhio-buildings.slim.geojson");
  const buildings = useMemo<FeatureCollection<Polygon | MultiPolygon, BuildingProperties> | null>(() => {
    if (!buildingsSlim) return null;
    return {
      type: "FeatureCollection",
      features: buildingsSlim.features.map((f) => ({
        type: "Feature" as const,
        properties: expandSlimBuilding(f.properties),
        geometry: f.geometry,
      })),
    };
  }, [buildingsSlim]);
  const roads = useGeoJson<FeatureCollection<LineString, RoadProps>>("/geo/sikhio-roads.geojson");
  // Sikhio has no BTS/MRT network — SRT railway handled by RailPanel

  // Hour + weekend state for the traffic simulation
  const [hour, setHour] = useState<number>(() => new Date().getHours());
  const [isWeekend, setIsWeekend] = useState<boolean>(() => {
    const d = new Date().getDay();
    return d === 0 || d === 6;
  });

  // Lens + per-layer toggles.
  // Default EXEC — the Mayor's briefing is the first screen, not OPS.
  // Staff switch to OPS from the lens palette for day-to-day ops.
  const [lens, setLens] = useState<LensId>("executive");
  const [mapViewState, setMapViewState] = useState<MapViewState>({ kind: "lens", lensId: "executive" });
  const [enabledLayers, setEnabledLayers] = useState<Set<LayerId>>(
    () => new Set(LENSES.find((l) => l.id === "executive")!.layers.filter((id) => layerCanEnable(id))),
  );
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  // The concierge's collapsed trigger is drawn in the bottom bar rather than
  // floating over the map — see ChatBox's Props docblock.
  const [chatOpen, setChatOpen] = useState(false);
  const [whitepaperOpen, setWhitepaperOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);
  // The computer-vision centre. Its own sheet — it is a surface the
  // operator opens, not a permanent rail section that would spend every
  // screen's pixels on something most screens never read.
  const [cvCentreOpen, setCvCentreOpen] = useState(false);
  // The camera wall's own open state, lifted so App knows whether the
  // CV feed is worth polling.
  const [cctvWallOpen, setCctvWallOpen] = useState(false);
  const [sheetsConfigured, setSheetsConfigured] = useState(() => {
    try { return Boolean(localStorage.getItem(SHEETS_STORAGE_KEY)); } catch (err) {
      // axiom-audit-ignore-next-line back -- localStorage private mode; UI shows the "not configured" hint correctly.
      if (typeof console !== "undefined") console.warn("[App] sheets-configured read failed", err);
      return false;
    }
  });
  // Forecast alerts — metrics whose p50 has breached alertThreshold
  const [forecastAlerts, setForecastAlerts] = useState<Set<string>>(new Set());
  const [forecastMetrics, setForecastMetrics] = useState<ForecastMetric[]>([]);

  // Mobile: 1-column stack with bottom segmented nav to swap panels.
  const isMobile = useIsMobile();
  const online = useOnlineStatus();
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>("map");

  // Mayor's Brief overlay — a focused, decision-first view that's distinct
  // from the mobile BRIEF slot. Auto-opens on small viewports by default
  // (URL ?mayor=1 forces on, ?mayor=0 forces off, ?brief=1 honoured for
  // shareable links). Persists user toggle to localStorage.
  const MAYOR_BRIEF_KEY = "sikhio:mayor-brief";
  const initialBriefMode = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash.replace(/^#/, "");
    if (params.get("mayor") === "1" || hash === "mayor" || params.get("brief") === "1") return true;
    if (params.get("mayor") === "0") return false;
    try {
      const saved = window.localStorage.getItem(MAYOR_BRIEF_KEY);
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch (err) {
      // axiom-audit-ignore-next-line back -- localStorage private mode; the fallback below (viewport check) is the correct default anyway.
      if (typeof console !== "undefined") console.warn("[App] brief-mode read failed", err);
    }
    // Default: open the Mayor's Brief when the viewport is too small for the
    // full dashboard (≤ 720 px) so a phone lands on the decision view, not
    // the map. Tablets and up get the full map by default.
    return typeof window !== "undefined" && window.matchMedia?.("(max-width: 720px)").matches;
  })();
  const [briefMode, setBriefMode] = useState<boolean>(initialBriefMode);
  const toggleBrief = useCallback(() => {
    setBriefMode((v) => {
      const next = !v;
      try { window.localStorage.setItem(MAYOR_BRIEF_KEY, next ? "1" : "0"); } catch (err) {
        // axiom-audit-ignore-next-line back -- localStorage private mode; the next render reads from state, not from storage.
        if (typeof console !== "undefined") console.warn("[App] brief-mode persist failed", err);
      }
      return next;
    });
  }, []);

  // MSH/CRASH: this callback used to be created INSIDE the `{briefMode && ...}`
  // block below, as a prop of <MayorsBrief>. That is a hooks-order
  // violation: the useCallback hook ran on every render where briefMode was
  // true and did not run at all where it was false, so React saw a different
  // number of hooks between renders and threw error #300 ("Rendered more
  // hooks than during the previous render"). The ErrorBoundary caught it and
  // replaced the whole dashboard with the "STM-01 · APPLICATION FAULT" screen.
  //
  // The path to it is the primary phone path: a phone lands on the Mayor's
  // Brief (auto-opens at <=720px), taps "กลับสู่แดชบอร์ด", and the app dies.
  // Measured live: brief open -> tap exit -> "APPLICATION FAULT", error
  // "Minified React error #300".
  //
  // A hook is called unconditionally at the top of a component, never inside
  // a condition. This one is hoisted to sit with the others.
  const openManual = useCallback(() => setManualOpen(true), []);

  // The same law, applied to the four triggers the top bar carries. They
  // were written as `onOpenX={useCallback(...)}` — a hook call sitting in a
  // JSX expression. Inside an UNconditional JSX tree that is merely ugly,
  // but the moment such a JSX tree moves behind a condition the hook count
  // changes between renders and React throws "Rendered more hooks than
  // during the previous render" (#310). The brief toggle was exactly that:
  // `onOpenChat={useCallback(...)}` sat inside `{briefMode && (...)}`, so
  // opening the brief on a desktop browser took the whole dashboard down
  // into the ErrorBoundary — shell gone, nothing readable, on the one
  // path the Mayor is supposed to trust.
  //
  // Hoisted here so the rule is absolute and checkable: no hook call
  // appears inside a JSX expression anywhere in this file. The gate in
  // scripts/msh-hook-order.mjs enforces exactly that.
  const openChat = useCallback(() => setChatOpen(true), []);
  const openCatalog = useCallback(() => setCatalogOpen(true), []);
  const openWhitepaper = useCallback(() => setWhitepaperOpen(true), []);
  const openSheets = useCallback(() => setSheetsOpen(true), []);

  // Lock background scroll while a mobile sheet is open.
  useEffect(() => {
    if (!isMobile || mobilePanel === "map") {
      document.body.style.removeProperty("overflow");
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobilePanel]);

  // Controlled map viewState — needed so building-search and click can fly the camera.
  // Tight bounds: the mayor should never accidentally scroll to Bangkok.
  const [viewState, setViewState] = useState({
    ...SIKHIO.defaultView,
    minZoom: 11,
    maxZoom: 20,
    transitionDuration: 0,
    maxBounds: SIKHIO.outerBounds,
  });

  // Mirror viewState to a ref so high-frequency reads (click handler, deck.gl
  // event callback) don't depend on stale React closures and don't force callback
  // recreation on every pan/zoom frame.
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  // rAF-throttled viewState mirror — DeckGL fires onViewStateChange potentially
  // many times per frame (high-refresh trackpads fire pointer events at 240 Hz+).
  // We coalesce those into ONE React state update per animation frame so the
  // ~1300-line App component re-renders at most 60 Hz, regardless of gesture rate.
  // The ref stays current immediately so any synchronous reader sees the latest.
  const pendingViewStateRef = useRef<typeof viewState | null>(null);
  const rafScheduledRef = useRef(false);
  const handleViewStateChange = useCallback(({ viewState: vs }: { viewState: Record<string, unknown> }) => {
    const longitude = vs.longitude as number;
    const latitude = vs.latitude as number;
    const zoom = vs.zoom as number;
    const pitch = vs.pitch as number;
    const bearing = vs.bearing as number;
    const next = {
      ...viewStateRef.current,
      longitude, latitude, zoom, pitch, bearing,
      transitionDuration: 0,
    };
    pendingViewStateRef.current = next;
    viewStateRef.current = next;
    if (rafScheduledRef.current) return;
    rafScheduledRef.current = true;
    requestAnimationFrame(() => {
      rafScheduledRef.current = false;
      const pending = pendingViewStateRef.current;
      pendingViewStateRef.current = null;
      if (pending) setViewState(pending);
    });
  }, []);

  // Selected building for the popup card.
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingProperties | null>(null);
  // [lng, lat] where the building was picked — feeds the Street View thumbnail.
  const [selectedCoord, setSelectedCoord] = useState<[number, number] | null>(null);
  // Selected incident — drives the IncidentCard with its "Open report ↗" link.
  const [selectedIncident, setSelectedIncident] = useState<IncidentFeature | null>(null);
  // Selected Sikhio CCTV camera — opens the iframe modal (NT msejub player).
  const [selectedCctv, setSelectedCctv] = useState<CctvCamera | null>(null);

  // Camera helpers
  const flyTo = useCallback((longitude: number, latitude: number, zoom = 17) => {
    setViewState((prev) => ({
      ...prev,
      longitude,
      latitude,
      zoom,
      transitionDuration: 700,
    }));
  }, []);
  // handleMapClick reads viewState from ref to keep the callback identity stable —
  // before this change it was recreated on every pan/zoom frame because viewState
  // coords were in the deps array, forcing deck.gl to rebind its onClick prop.
  const handleMapClick = useCallback((info: { layer?: { id?: string } | null; object?: unknown; coordinate?: number[] }) => {
    if (info.layer?.id === "municipality-buildings" && info.object) {
      const f = info.object as { properties: BuildingProperties; geometry: { coordinates: number[][][] | number[][][][] } };
      setSelectedIncident(null);
      setSelectedCctv(null);
      setSelectedBuilding(f.properties);
      const vs = viewStateRef.current;
      const [lng, lat] = info.coordinate ?? [vs.longitude, vs.latitude];
      setSelectedCoord([lng, lat]);
      flyTo(lng, lat, Math.max(vs.zoom, 17));
    } else if ((info.layer?.id === "incidents-city-reports" || info.layer?.id === "incidents-itic") && info.object) {
      // Click an incident <Arrow direction="right" /> open its card with a deep-link back to the report.
      setSelectedBuilding(null);
      setSelectedCctv(null);
      setSelectedIncident(info.object as IncidentFeature);
    } else if (info.layer?.id === "cctv-cameras" && info.object) {
      // Click a CCTV marker — every vendor opens a unified live-view modal:
      //   Sikhio NT to iframe to the msejub player (intranet-gated)
      //   Longdo    to JPG snapshot viewer + Refresh + HLS link fallback
      const cam = info.object as CctvCamera;
      setSelectedBuilding(null);
      setSelectedIncident(null);
      setSelectedCctv(cam);
    } else if (!info.layer) {
      setSelectedBuilding(null);
      setSelectedIncident(null);
      setSelectedCctv(null);
    }
  }, [flyTo]);

  const zoomBy = (delta: number) => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.minZoom ?? 3, Math.min(prev.maxZoom ?? 20, prev.zoom + delta)),
      transitionDuration: 200,
    }));
  };

  // Two view modes: 2D plan (pitch 0, basemap picker available) and 3D
  // (buildings extruded). The old third "3DS" substructure mode ghosted the
  // buildings to reveal utility pipes at burial depth — those utility layers
  // no longer exist, so it rendered an empty ghost city and has been removed.
  type ViewMode = "2D" | "3D";
  const [viewMode, setViewMode] = useState<ViewMode>("3D");
  const is3D = viewMode === "3D";

  const cycleViewMode = useCallback(() => {
    setViewMode((prevMode) => (prevMode === "2D" ? "3D" : "2D"));
  }, []);

  useEffect(() => {
    setViewState((prev) => ({
      ...prev,
      pitch: viewMode === "2D" ? 0 : 60,
      bearing: viewMode === "2D" ? 0 : -18,
      transitionDuration: 700,
    }));
  }, [viewMode]);

  // ESC closes the topmost overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (manualOpen) setManualOpen(false);
      else if (whitepaperOpen) setWhitepaperOpen(false);
      else if (catalogOpen) setCatalogOpen(false);
      else if (selectedIncident) setSelectedIncident(null);
      else if (selectedCctv) setSelectedCctv(null);
      else if (selectedBuilding) setSelectedBuilding(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [catalogOpen, selectedBuilding, selectedIncident, selectedCctv, manualOpen, whitepaperOpen]);

  // Lookup table for hover tooltips — keeps DeckGL declarative.
  const tooltipForPickMemo = useCallback((info: { layer?: { id?: string } | null; object?: unknown }) => {
    const o = info.object as Record<string, unknown> | undefined;
    if (!info.layer || !o) return null;
    const id = info.layer.id;
    const p = (o as { properties?: Record<string, unknown> }).properties ?? o;
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = (p as Record<string, unknown>)[k];
        if (typeof v === "string" && v.trim()) return v;
        if (typeof v === "number") return String(v);
      }
      return null;
    };
    let title: string | null = null;
    let sub: string | null = null;
    switch (id) {
      case "municipality-buildings":
        title = pick("nameEn", "name", "nameTh") ?? "Building";
        sub = (() => {
          const lvls = (p as { levels?: number }).levels;
          const h = (p as { height?: number }).height;
          if (h) return `${h} m tall`;
          if (lvls) return `${lvls} floors`;
          return null;
        })();
        break;
      case "incidents-itic":
      case "incidents-city-reports":
        title = pick("title") ?? "Incident";
        sub = pick("category", "severity", "status");
        break;
      case "cctv-cameras":
        title = pick("name") ?? "CCTV";
        sub = pick("vendor");
        break;
      case "civic-points": {
        const kind = (p as { kind?: string }).kind ?? "other";
        const palette = CIVIC_PALETTE[kind as CivicKind] ?? CIVIC_PALETTE.other;
        const nm = pick("name:en", "name", "name:th") ?? palette.label;
        title = `${palette.glyph} ${nm}`;
        sub = palette.label + (pick("operator") ? ` · ${pick("operator")}` : "");
        break;
      }
      case "waterways": {
        const wt = (p as { waterway?: string }).waterway ?? "stream";
        const nm = pick("name:en", "name", "name:th") ?? wt;
        title = nm;
        sub = wt.toUpperCase() + (pick("intermittent") === "yes" ? " · intermittent" : "");
        break;
      }
      case "datago-points":
        title = pick("name", "nameEn") ?? "data.go.th POI";
        sub = `${(p as { category?: string }).category ?? ""} · ${(p as { source?: string }).source ?? ""}`;
        break;
      case "air4thai-stations": {
        const pm25 = (p as { pm25?: number | null }).pm25;
        const aqi = (p as { aqi?: number | null }).aqi;
        title = (p as { station?: string }).station ?? "Air4Thai station";
        sub = `PM2.5 ${pm25 ?? "—"} µg/m³ · AQI ${aqi ?? "—"} · PCD`;
        break;
      }
      case "attractions": {
        const a = p as { name?: string; nameEn?: string; category?: string; describe?: string };
        title = a.name ?? "แหล่งท่องเที่ยว";
        sub = `${a.category ?? ""}${a.nameEn ? ` · ${a.nameEn}` : ""}${a.describe ? ` — ${a.describe}` : ""}`;
        break;
      }
      case "health-tambons": {
        const t = p as {
          name?: string; population?: number; vhvCount?: number;
          elderlyBedridden?: number; elderlyHomebound?: number; familyDoctorTeamCount?: number;
        };
        title = `ต.${t.name ?? "—"}`;
        sub = `${(t.population ?? 0).toLocaleString()} คน · อสม. ${t.vhvCount ?? "—"} · ติดบ้าน ${t.elderlyHomebound ?? "—"} · ติดเตียง ${t.elderlyBedridden ?? "—"}`;
        break;
      }
      case "lam-takhong": {
        const w = p as {
          pointType?: string; name?: string; pctNormal?: number; storageMcm?: number;
          kind?: string; waterlevelMsl?: number | null; freeboardM?: number | null;
          situationLevel?: number | null; rain24hMm?: number | null; river?: string | null;
        };
        title = w.name ?? "สถานีวัดน้ำ";
        if (w.pointType === "dam") {
          sub = `เขื่อน · ${w.pctNormal?.toFixed(1) ?? "—"}% ของ รนก. · ${w.storageMcm?.toFixed(1) ?? "—"} ล้าน ม.³`;
        } else if (w.kind === "rain") {
          sub = `ฝน 24 ชม. ${w.rain24hMm ?? "—"} มม. · HII`;
        } else {
          sub = `ระดับน้ำ ${w.waterlevelMsl?.toFixed(2) ?? "—"} ม.รทก. · ต่ำกว่าตลิ่ง ${w.freeboardM?.toFixed(2) ?? "—"} ม. · L${w.situationLevel ?? "—"}${w.river ? ` · ${w.river}` : ""}`;
        }
        break;
      }
      case "news-pins": {
        const tags = (p as { tags?: string[] }).tags ?? [];
        title = pick("title") ?? "News";
        sub = `${tags.join(" ")} · ${pick("source") ?? ""}`;
        break;
      }
      case "temple-spires-base":
      case "old-town-district":
        title = pick("name") ?? pick("nameTh") ?? "Heritage site";
        sub = pick("era") ?? null;
        break;
      case "flood-risk":
      case "flood-risk-zones":
        title = pick("name") ?? "Flood-prone area";
        sub = `${(pick("severity") ?? "").toUpperCase()} · ${pick("type") ?? ""}${pick("households") ? ` · ${pick("households")} households` : ""}`;
        break;
      default:
        return null;
    }
    // CCTV markers get an extra "click to view" line so the operator
    // knows the marker is interactive. Detected by switching on the
    // camera row we set up above.
    const isCctv = id === "cctv-cameras";
    return {
      html:
        `<div class="picker-tooltip${isCctv ? " picker-tooltip--cta" : ""}"><div class="picker-title">${escapeHtml(title ?? "")}</div>` +
        (sub ? `<div class="picker-sub">${escapeHtml(sub)}</div>` : "") +
        (isCctv ? `<div class="picker-cta">คลิกเพื่อดูภาพ · Click to view</div>` : "") +
        `</div>`,
      style: {
        background: "transparent",
        border: "none",
        padding: "0",
      },
    };
  }, []);

  const onLensChange = useCallback((id: LensId) => {
    setLens(id);
    setMapViewState({ kind: "lens", lensId: id });
    const next = LENSES.find((l) => l.id === id);
    if (next) setEnabledLayers(new Set(next.layers.filter((layerId) => layerCanEnable(layerId))));
    setForecastAlerts(new Set()); // clear stale alert badges on lens switch
  }, []);

  const handleForecastMetricClick = useCallback((metric: string) => {
    const entry = METRIC_LAYER_MAP[metric];
    if (!entry) return;
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      entry.layers.forEach((id) => next.add(id));
      return next;
    });
    // Water / basin metrics fly to Lam Takhong — never the old Chonburi coast.
    if (entry.flyToCoast) flyTo(101.5617, 14.865, 13.5);
  }, [flyTo]);

  const handleForecastAlert = useCallback((metric: string) => {
    setForecastAlerts((prev) => prev.has(metric) ? prev : new Set([...prev, metric]));
  }, []);
  // Transient toast — gives a layer toggle visible feedback when it would
  // otherwise render nothing (no features yet, or a feed that needs an API key).
  // Kills the "I clicked it and nothing happened" embarrassment.
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => { if (toastTimer.current) window.clearTimeout(toastTimer.current); }, []);

  // Latest layer feedback (counts + feed health), read by onToggleLayer without
  // making the callback depend on every feed poll. Assigned during render below.
  const layerFeedbackRef = useRef<{
    counts: Record<string, number>;
    statuses: Partial<Record<LayerId, LayerStatus>>;
    enabled: Set<LayerId>;
  }>({ counts: {}, statuses: {}, enabled: new Set() });

  const onToggleLayer = useCallback((id: LayerId) => {
    const fb = layerFeedbackRef.current;
    const turningOn = !fb.enabled.has(id);
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (turningOn) {
      const label = ALL_LAYERS.find((l) => l.id === id)?.label ?? id;
      const status = fb.statuses[id];
      const count = fb.counts[id];
      if (status?.tier === "unavailable") {
        showToast(`${label} — no live data (${status.note ?? "needs API key"})`);
      } else if (count === 0) {
        showToast(`${label} — no features to show right now`);
      }
    }
  }, [showToast]);
  const restoreLens = useCallback(() => {
    const next = LENSES.find((l) => l.id === lens);
    setMapViewState({ kind: "lens", lensId: lens });
    if (next) setEnabledLayers(new Set(next.layers.filter((layerId) => layerCanEnable(layerId))));
  }, [lens]);
  // AERIAL and CLEAR were byte-identical, so two buttons promising different
  // things did the same thing. They now differ as their labels imply:
  // AERIAL drops to imagery + boundary in plan view; CLEAR strips every data
  // overlay but leaves the basemap and camera exactly as the operator set them.
  const setAerialOnly = useCallback(() => {
    setViewMode("2D");
    setBasemapId("satellite");
    setMapViewState({ kind: "custom", label: "ภาพถ่ายดาวเทียม" });
    setEnabledLayers(new Set<LayerId>(["satellite-esri", "municipality-boundary-line"]));
  }, []);
  const clearOverlays = useCallback(() => {
    setMapViewState({ kind: "custom", label: "ซ่อนเลเยอร์ทั้งหมด" });
    setEnabledLayers(new Set<LayerId>(["municipality-boundary-line"]));
  }, []);

  // Feeds
  const cityReports = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/city-reports`, 5 * 60_000);
  const iticEvents = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/itic`, 3 * 60_000);
  const news = useFeed<IntelligenceItem>(`${API_BASE}/api/news`, 3 * 60_000);
  const weather = useFeed<WeatherSnapshot>(`${API_BASE}/api/weather`, 30 * 60_000);
  const airQuality = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality`, 15 * 60_000);
  const air4thai = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality/air4thai`, 30 * 60_000);
  const cctv = useFeed<CctvCamera>(`${API_BASE}/api/cctv/longdo`, 10 * 60_000);
  // Sikhio municipality's own CCTV system (NT CCTV@R2). Distinct feed because
  // the upstream requires session auth and uses a different protocol (MSE
  // WebSocket on port 5080 vs Longdo's HTTP JPG/HLS). The two arrays are
  // merged into the single "cctv-cameras" map layer below; IDs are vendor-
  // prefixed upstream so collisions are impossible.
  const cctvSikhio = useFeed<CctvCamera>(`${API_BASE}/api/cctv/sikhio`, 10 * 60_000);
  const allCctv = useMemo<CctvCamera[]>(
    () => [...cctv.data, ...cctvSikhio.data],
    [cctv.data, cctvSikhio.data],
  );
  // The per-camera computer-vision state. Polled ONLY while a surface that
  // shows it is on screen — the wall is collapsed by default, so polling
  // unconditionally would fetch bytes nobody is looking at.
  const cvCentres = useCvCentres(API_BASE, cvCentreOpen || cctvWallOpen);
  const aqiTrend = useFeed<AqiTrend>(`${API_BASE}/api/air-quality/trend`, 15 * 60_000);
  const trends = useFeed<TrendsSnapshot>(`${API_BASE}/api/trends`, 15 * 60_000);
  const executive = useFeed<ExecutiveSnapshot>(`${API_BASE}/api/executive`, 5 * 60_000);
  const datago = useFeed<DatagoPoint>(`${API_BASE}/api/datago/points`, 30 * 60_000);
  const facebook = useFeed<FacebookPost>(`${API_BASE}/api/social/facebook`, 10 * 60_000);
  const reservoirs = useFeed<ReservoirStatus>(`${API_BASE}/api/datago/reservoirs`, 60 * 60_000);
  const lamTakhong = useFeed<DamReading>(`${API_BASE}/api/lam-takhong`, 15 * 60_000);
  const basinWater = useFeed<BasinStation>(`${API_BASE}/api/basin-water`, 5 * 60_000);
  const drought = useFeed<DroughtBrief>(`${API_BASE}/api/drought`, 15 * 60_000);
  const floodDash = useFeed<FloodDashBrief>(`${API_BASE}/api/flooddash`, 5 * 60_000);
  const airDash = useFeed<AirDashBrief>(`${API_BASE}/api/airdash`, 15 * 60_000);
  // Civic reference data — changes on the order of years, so poll lazily.
  // 24h-TTL reference feeds are deferred by useDeferredReady(800ms) so the
  // map and water-security hero get the network first.
  const mophHealth = useFeed<HealthTambon>(
    referenceReady ? `${API_BASE}/api/moph-health` : null, 12 * 60 * 60_000);
  const attractions = useFeed<Attraction>(
    referenceReady ? `${API_BASE}/api/attractions` : null, 24 * 60 * 60_000);
  const muniProfile = useFeed<MunicipalityProfile>(
    referenceReady ? `${API_BASE}/api/municipality-profile` : null, 24 * 60 * 60_000);
  const agriculture = useFeed<CropPrice>(`${API_BASE}/api/agriculture`, 6 * 60 * 60_000);
  const khaoYai = useFeed<KhaoYaiStatus>(`${API_BASE}/api/khao-yai`, 6 * 60 * 60_000);
  const railway = useFeed<TrainService>(`${API_BASE}/api/railway`, 60 * 60_000);
  const waste = useFeed<WasteStatus>(`${API_BASE}/api/waste`, 6 * 60 * 60_000);
  const elderCare = useFeed<ElderCareStatus>(`${API_BASE}/api/elder-care`, 6 * 60 * 60_000);
  const highway = useFeed<HighwayStatus>(`${API_BASE}/api/highway`, 5 * 60_000);
  const localMarket = useFeed<LocalMarketData>(`${API_BASE}/api/local-market`, 30 * 60_000);
  const provincialKPIs = useFeed<ProvincialKPIsType>(`${API_BASE}/api/datago/provincial-kpis`, 6 * 60 * 60_000);
  const nasaEarth = useFeed<NasaEarthReadings>(`${API_BASE}/api/nasa/earth-readings`, 6 * 60 * 60_000);
  const academic = { data: [] as AcademicSnapshot[] };

  // Civic POIs + waterways (municipality OSM)
  const civicPoints = useGeoJson<FeatureCollection<Point, Record<string, unknown>>>(
    "/geo/sikhio-civic.geojson",
  );
  const waterways = useGeoJson<FeatureCollection<LineString, Record<string, unknown>>>(
    "/geo/sikhio-waterways.geojson",
  );
  const waterBodies = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/sikhio-water-bodies.geojson",
  );
  const railwayLines = useGeoJson<FeatureCollection<LineString, Record<string, unknown>>>(
    "/geo/sikhio-railway.geojson",
  );
  // Cursor lat/lng HUD (ArcGIS-style). Throttled via rAF in the hover handler.
  const [cursorLngLat, setCursorLngLat] = useState<{ lng: number; lat: number } | null>(null);
  const cursorRaf = useRef<number | null>(null);
  const heritage = useGeoJson<FeatureCollection<Point | Polygon | MultiPolygon, HeritageFeatureProps>>(
    "/geo/sikhio-heritage.geojson",
  );
  const floodRisk = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/sikhio-flood-risk.geojson",
  );

  // Traffic samples — recomputed when hour or roads change
  const trafficSamples = useMemo(() => {
    if (!roads) return [];
    return buildTrafficSamples(roads, hour, { isWeekend });
  }, [roads, hour, isWeekend]);

  // Photorealistic 3D Tiles (Google) — must be called at top level (Rules of Hooks)
  const tile3d = useTile3DLayer({
    visible: enabledLayers.has("tile3d-buildings"),
    source: "google",
  });

  // Google Map Tiles sessions — minted lazily when the layer is first enabled.
  const googleSatSession = useGoogleTileSession("satellite", enabledLayers.has("google-satellite"));
  const googleTrafficSession = useGoogleTileSession("traffic", enabledLayers.has("google-traffic"));

  // Quantized zoom level — only changes when crossing the discrete thresholds
  // used for maxRoofs. Prevents the layers useMemo from firing on every zoom tick.
  const zoomBucket = viewState.zoom >= 16.5 ? 2 : viewState.zoom >= 15.2 ? 1 : 0;

  // Pre-memoize the two largest layers (~10k MS+OSM buildings). The umbrella
  // `layers` memo below has ~40 deps including SWR feed polls — if any of those
  // View-bound building pre-filter. Without this, deck.gl was
  // re-tessellating all 9,900 buildings on every pan/zoom — that was
  // the source of the lag. The filter is memoised on a coarse viewport
  // key (4-decimal lng/lat, half-step zoom) so a sub-meter pan doesn't
  // force a layer rebuild.
  const vpKey = viewportKey(viewState);
  const buildingsInView = useMemo(() => {
    if (!buildings) return null;
    return filterBuildingsToViewport(buildings, viewState);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- viewState is the right dep; vpKey forces a rebuild on coarse movement
  }, [buildings, viewState, vpKey]);

  // change, the buildings/roofs GeoJsonLayer would otherwise be re-instantiated
  // and deck.gl would re-tessellate. With a stable reference, deck.gl sees the
  // same layer object and skips the diff entirely.
  const memoizedBuildingsLayer = useMemo<Layer | null>(
    () => buildingsInView ? (buildingsLayer(buildingsInView, { extruded: is3D }) as Layer) : null,
    [buildingsInView, is3D],
  );

  const layers = useMemo<Layer[]>(() => {
    const out: Layer[] = [];
    // Imagery first — renders beneath all vector data
    // Esri is the only satellite useful at city zoom — high-res tiles up to 19.
    if (enabledLayers.has("satellite-esri")) out.push(esriSatelliteLayer(1.0) as Layer);
    // Satellite rasters — GIBS layers now served via MapLibre <Source>/<Layer>
    // (see MapLibreMap children below) for reliable tile stretching at any zoom.
    // Only Esri HD (maxZoom:19), Himawari (WMS), and terrain stay in deck.gl.
    if (enabledLayers.has("satellite-terrain")) out.push(openTopoTerrainLayer(0.6) as Layer);
    if (enabledLayers.has("satellite-himawari")) out.push(himawariInfraredLayer(0.55) as Layer);
    const showBoundaryFill =
      enabledLayers.has("municipality-boundary-fill") ||
      enabledLayers.has("municipality-boundary");
    const showBoundaryLine = enabledLayers.has("municipality-boundary-line") || showBoundaryFill;
    if ((showBoundaryLine || showBoundaryFill) && campus)
      out.push(campusBoundaryLayer(campus, { filled: showBoundaryFill, stroked: showBoundaryLine }) as Layer);
    if (enabledLayers.has("municipality-buildings") && memoizedBuildingsLayer)
      out.push(memoizedBuildingsLayer);
    // Photorealistic 3D Tiles (Google) — textured glTF mesh streamed by deck.gl
    if (tile3d.layer) out.push(tile3d.layer as Layer);
    if (enabledLayers.has("road-network") && roads)
      out.push(roadNetworkLayer(roads as unknown as FeatureCollection<LineString, ClassifiedRoadProps>) as Layer);
    if (enabledLayers.has("traffic-heatmap") && trafficSamples.length > 0) out.push(trafficHeatmapLayer(trafficSamples) as Layer);
    if (enabledLayers.has("cctv-cameras")) out.push(cctvLayer(allCctv) as Layer);
    if (enabledLayers.has("incidents-city-reports")) out.push(incidentLayer("incidents-city-reports", cityReports.data) as Layer);
    if (enabledLayers.has("incidents-itic")) out.push(incidentLayer("incidents-itic", iticEvents.data) as Layer);
    // Open data
    if (enabledLayers.has("datago-points") && datago.data.length > 0) out.push(datagoPointsLayer(datago.data) as Layer);
    // Air4Thai PCD stations (official Thai government AQ monitors, Korat bbox)
    if (enabledLayers.has("air4thai-stations") && air4thai.data.length > 0) out.push(air4thaiLayer(air4thai.data) as Layer);
    // GISTDA POI Digital Twin (authoritative Thai government POIs)
    // GISTDA LOD2 Solar Irradiance (building rooftop solar potential)
    // GISTDA Land Use / Land Cover
    // Lam Takhong water: dam + HII basin gauges. Pushed after the area layers
    // (land-use, solar) so the point markers stay visible above them.
    if (enabledLayers.has("lam-takhong") && (basinWater.data.length > 0 || lamTakhong.data.length > 0)) {
      out.push(lamTakhongLayer(basinWater.data, lamTakhong.data[0] ?? null) as Layer);
      // Upstream <Arrow direction="right" /> dam flow lines — thin dashed lines from each HII
      // water-level gauge to the dam, coloured by the gauge's situation
      // level. Reads as "this is where the water is coming from" without
      // a separate legend. Decoupled from the gauge dots so the dots
      // remain visible.
      const upstream = upstreamToDamLayer(basinWater.data, lamTakhong.data[0] ?? null);
      if (upstream) out.push(upstream as Layer);
      // Dam outflow indicator — only renders when the dam is releasing
      // (releasedMcmDay > 0.05). A small bright pulse ~1.5 km downstream
      // reads as "the dam is releasing water" without a separate legend.
      const outflow = damOutflowLayer(lamTakhong.data[0] ?? null, waterways ?? null);
      if (outflow) out.push(outflow as Layer);
    }
    // News pins — geocoded headlines so the mayor sees "which market" at a glance
    if (enabledLayers.has("news-pins") && news.data.length > 0) out.push(newsPinsLayer(news.data) as Layer);
    // Civic POIs (province-wide OSM: hospitals/schools/police/fire/temples/markets/...)
    if (enabledLayers.has("civic-points") && civicPoints) out.push(civicPointsLayer(civicPoints) as Layer);
    // Civic reference layers — tourism/heritage POIs and per-tambon primary care.
    if (enabledLayers.has("health-tambons") && mophHealth.data.length > 0) out.push(healthTambonLayer(mophHealth.data) as Layer);
    if (enabledLayers.has("attractions") && attractions.data.length > 0) out.push(attractionsLayer(attractions.data) as Layer);
    // Waterways (canals + rivers + drains). The water-flow arrows layer
    // shows direction on rivers only — it's the "God Mode" affordance
    // that makes the user instantly see "this is which way the water
    // is moving." Toggle it with the same waterways switch so users
    // never see one without the other.
    if (enabledLayers.has("waterways") && waterways) {
      out.push(waterwaysLayer(waterways) as Layer);
      out.push(waterFlowArrowsLayer(waterways) as Layer);
    }
    if (enabledLayers.has("water-bodies") && waterBodies) out.push(waterBodiesLayer(waterBodies) as Layer);
    if (enabledLayers.has("railway") && railwayLines) out.push(railwayLayer(railwayLines) as Layer);
    // Heritage: explicit toggles so operators can remove these overlays for a clean aerial view.
    if (heritage) {
      const districtFc = {
        type: "FeatureCollection" as const,
        features: heritage.features.filter(f => f.properties.kind === "old-town-district"),
      } as FeatureCollection<Polygon | MultiPolygon, HeritageFeatureProps>;
      const spiresFc = {
        type: "FeatureCollection" as const,
        features: heritage.features.filter(f => f.properties.kind !== "old-town-district"),
      } as FeatureCollection<Point, HeritageFeatureProps>;
      if (enabledLayers.has("heritage-old-town")) {
        const dist = oldTownDistrictLayer(districtFc);
        if (dist) out.push(dist as Layer);
      }
      if (enabledLayers.has("heritage-temple-spires")) {
        out.push(...(templeSpiresLayer(spiresFc) as Layer[]));
      }
    }
    // Flood-risk polygons — color escalates with Lam Takhong storage.
    // When the dam is at or above normal high, downstream flood zones
    // tint redder and more opaque so the user can see the situation
    // waking up. updateTriggers on the layer handle the re-render.
    if (enabledLayers.has("flood-risk-zones") && floodRisk) {
      out.push(
        floodRiskLayer(floodRisk, { damPctNormal: lamTakhong.data[0]?.pctNormal ?? null }) as Layer,
      );
    }
    // Distance grid (1·5·10 km)
    if (enabledLayers.has("distance-grid")) {
      out.push(distanceGridLayer(SIKHIO.center, [1, 5, 10]) as Layer);
      out.push(distanceGridLabelsLayer(SIKHIO.center, [1, 5, 10]) as Layer);
    }

    return out;
  }, [
    enabledLayers, zoomBucket, is3D, campus, buildings,
    memoizedBuildingsLayer,
    trafficSamples,
    roads,
    allCctv, cityReports.data,
    iticEvents.data,
    civicPoints, waterways, waterBodies, railwayLines, floodRisk, heritage,
    datago.data,
    news.data,
    basinWater.data, lamTakhong.data, attractions.data, mophHealth.data,
    tile3d.layer,
  ]);

  // Feature counts — passed to LayerPalette so every toggle shows a number,
  // making it immediately obvious whether the layer has data or not.
  const layerCounts = useMemo(() => ({
    "municipality-boundary-line": campus?.features.length ?? 0,
    "municipality-boundary-fill": campus?.features.length ?? 0,
    "municipality-boundary":      campus?.features.length ?? 0,
    "municipality-buildings": buildings?.features.length ?? 0,
    "heritage-old-town":      heritage?.features.filter((f) => f.properties.kind === "old-town-district").length ?? 0,
    "heritage-temple-spires": heritage?.features.filter((f) => f.properties.kind !== "old-town-district").length ?? 0,
    "road-network":           roads?.features.length ?? 0,
    "civic-points":           civicPoints?.features.length ?? 0,
    "waterways":              waterways?.features.length ?? 0,
    "water-bodies":           waterBodies?.features.length ?? 0,
    "railway":                railwayLines?.features.length ?? 0,
    "flood-risk-zones":       floodRisk?.features.length ?? 0,
    "cctv-cameras":           allCctv.length,
    "incidents-itic":         iticEvents.data.length,
    "incidents-city-reports": cityReports.data.length,
    "datago-points":          datago.data.length,
    "air4thai-stations":      air4thai.data.length,
    "lam-takhong":            basinWater.data.length + lamTakhong.data.length,
    "attractions":            attractions.data.length,
    "health-tambons":         mophHealth.data.length,
    "news-pins":              news.data.filter((n) => n.lat != null).length,
  } as Record<string, number>), [
    campus, buildings, roads, civicPoints, waterways,
    floodRisk, heritage,
    cctv.data, iticEvents.data, cityReports.data, datago.data,
    air4thai.data, news.data,
    basinWater.data, lamTakhong.data, attractions.data, mophHealth.data,
  ]);

  // Feed health per map layer — drives the "needs key" pill in the palette and
  // the toast on toggle. Only layers backed by a live feed appear here.
  // Longdo + Sikhio NT are independent feeds: combine by taking the WORSE
  // tier (unavailable > scenario > cache > database > live) so a single
  // broken upstream shows the "needs attention" pill rather than a false
  // green.
  const TIER_ORDER: FallbackTier[] = ["unavailable", "scenario", "cache", "database", "live"];
  // useFeed returns `FallbackTier | "loading"` — "loading" is what you see
  // before the first fetch resolves, so it has to flow through.
  const cctvTier: FallbackTier | "loading" =
    (TIER_ORDER.find((t) => t === cctv.fallbackTier || t === cctvSikhio.fallbackTier) as FallbackTier | undefined) ??
    cctv.fallbackTier;
  const cctvNote = cctv.note ?? cctvSikhio.note ?? undefined;
  const layerStatuses = useMemo<Partial<Record<LayerId, LayerStatus>>>(() => ({
    "cctv-cameras":           { tier: cctvTier, note: cctvNote },
    "incidents-itic":         { tier: iticEvents.fallbackTier, note: iticEvents.note },
    "incidents-city-reports": { tier: cityReports.fallbackTier, note: cityReports.note },
    "datago-points":          { tier: datago.fallbackTier, note: datago.note },
    "air4thai-stations":      { tier: air4thai.fallbackTier, note: air4thai.note },
    "lam-takhong":            { tier: basinWater.fallbackTier, note: basinWater.note },
    "attractions":            { tier: attractions.fallbackTier, note: attractions.note },
    "health-tambons":         { tier: mophHealth.fallbackTier, note: mophHealth.note },
    "news-pins":              { tier: news.fallbackTier, note: news.note },
  }), [
    cctvTier, cctvNote,
    iticEvents.fallbackTier, iticEvents.note, cityReports.fallbackTier, cityReports.note,
    datago.fallbackTier, datago.note, air4thai.fallbackTier, air4thai.note,
    basinWater.fallbackTier, basinWater.note,
    attractions.fallbackTier, attractions.note,
    mophHealth.fallbackTier, mophHealth.note,
    news.fallbackTier, news.note,
  ]);

  // Keep the toggle handler's view of feedback fresh without re-creating it.
  layerFeedbackRef.current = { counts: layerCounts, statuses: layerStatuses, enabled: enabledLayers };


  // Thai-first feed-health chips. Proper nouns (iTIC, CCTV) keep their
  // original Latin form — they're external system names. Everything else
  // is Thai, including abbreviations the dashboard invented (NEWS / CR /
  // AQ / WX / DAM / BASIN / DRGT / FLD / EX become ข่าว / รายงาน /
  // อากาศ / สภาพอากาศ / เขื่อน / ลุ่มน้ำ / ภัยแล้ง / น้ำท่วม / สรุป).
  const feedHealth = useMemo(() => [
    { label: "ข่าว", tier: news.fallbackTier, ageMinutes: news.ageMinutes },
    { label: "รายงาน", tier: cityReports.fallbackTier, ageMinutes: cityReports.ageMinutes },
    { label: "iTIC", tier: iticEvents.fallbackTier, ageMinutes: iticEvents.ageMinutes },
    // Combined CCTV badge: worst tier across Longdo + Sikhio NT (unavailable
    // beats live), stalest ageMinutes so operators see "the older of the two
    // feeds" rather than a false-fresh reading from one feed.
    { label: "CCTV", tier: cctvTier, ageMinutes: Math.max(cctv.ageMinutes, cctvSikhio.ageMinutes) },
    { label: "ข้อมูล", tier: datago.fallbackTier, ageMinutes: datago.ageMinutes },
    { label: "อากาศ", tier: aqiTrend.fallbackTier, ageMinutes: aqiTrend.ageMinutes },
    { label: "สภาพอากาศ", tier: weather.fallbackTier, ageMinutes: weather.ageMinutes },
    { label: "เขื่อน", tier: lamTakhong.fallbackTier, ageMinutes: lamTakhong.ageMinutes },
    { label: "ลุ่มน้ำ", tier: basinWater.fallbackTier, ageMinutes: basinWater.ageMinutes },
    { label: "ภัยแล้ง", tier: drought.fallbackTier, ageMinutes: drought.ageMinutes },
    { label: "น้ำท่วม", tier: floodDash.fallbackTier, ageMinutes: floodDash.ageMinutes },
    { label: "สรุป", tier: executive.fallbackTier, ageMinutes: executive.ageMinutes },
  ], [news.fallbackTier, news.ageMinutes, cityReports.fallbackTier, cityReports.ageMinutes, iticEvents.fallbackTier, iticEvents.ageMinutes, cctvTier, cctv.ageMinutes, cctvSikhio.ageMinutes, datago.fallbackTier, datago.ageMinutes, aqiTrend.fallbackTier, aqiTrend.ageMinutes, weather.fallbackTier, weather.ageMinutes, lamTakhong.fallbackTier, lamTakhong.ageMinutes, basinWater.fallbackTier, basinWater.ageMinutes, drought.fallbackTier, drought.ageMinutes, floodDash.fallbackTier, floodDash.ageMinutes, executive.fallbackTier, executive.ageMinutes]);

  // Water detail panels (FloodRisk / Drought) for AGRI/ENV/EAR/SAF/INT drill-down.
  // EXEC always keeps the watershed rail visible — including honest FEED GAP shells.
  const showWaterDetail =
    lens === "agri" || lens === "environment" || lens === "earth" ||
    lens === "safety" || lens === "intelligence";
  const showWatershed = showWaterDetail || lens === "executive";
  // The panel appears once the feed has data. This used to carry the note
  // "Google Trends is Node-only; on the Worker it returns empty scenario
  // snapshots" — that was wrong: the deployed worker returns live data
  // (meta.fallbackTier "live", 91 interest-over-time points per language).
  // It is not parked above the Mayor's news desk while broken; it arrives
  // when it has something to show.
  //
  // It is still sometimes absent because the feed genuinely FAILS: measured
  // against the deployed worker, one request came back fallbackTier
  // "scenario" with 0 interest points and an upstream JSON parse error
  // ("Unexpected token 'l', \"l lang=\"en\"..."). The worker is not
  // reliable, so the panel's absence is not proof that it is still loading.
  const trendsHasSignal = trends.data.some((s) => s.interestOverTime.length > 0);

  return (
    <div
      className={[
        "shell",
        isMobile ? `mobile mobile-panel-${mobilePanel}` : "",
        briefMode ? "brief-mode" : "",
      ].filter(Boolean).join(" ")}
      data-mobile={isMobile ? "true" : "false"}
      data-brief={briefMode ? "true" : "false"}
    >
      {/* WCAG 2.4.1 — let keyboard users bypass the topbar (10+ buttons)
          and the feed chips to land on the data rail. Renders off-screen
          until focused, then becomes a single visible pill at the top-left. */}
      <a href="#main-content" className="skip-link mono">
        ข้ามไปยังข้อมูลหลัก · Skip to main content
      </a>
      {!online && (
        <div className="offline-banner mono" role="alert" aria-live="assertive">
          ⚠ OFFLINE — feeds are stale until the connection returns
        </div>
      )}
      {toast && (
        <div className="layer-toast mono" role="status" aria-live="polite">
          {toast}
        </div>
      )}
      {/* ── Top bar ── */}
      <MemoTopBar        feeds={feedHealth}
        onOpenCatalog={openCatalog}
        catalogCount={ALL_LAYERS.length}
        viewMode={viewMode}
        onCycleViewMode={cycleViewMode}
        onOpenManual={openManual}
        onOpenWhitepaper={openWhitepaper}
        onOpenSheets={openSheets}
        sheetsConfigured={sheetsConfigured}
        academic={academic.data[0] ?? null}
        systemStatus={systemHealth?.system.status ?? "unknown"}
        briefMode={briefMode}
        onToggleBrief={toggleBrief}
      />

      {/* ── Mayor's Brief overlay ──
          A focused, mobile-first view that replaces the map + rails when
          the user (typically the Mayor on a phone) wants one decision,
          not thirty feeds. Triggered by the topbar toggle, by ?mayor=1 /
          #mayor in the URL, or auto-shown on viewports ≤ 720 px wide. */}
      {briefMode && (
        <MayorsBrief
          dam={lamTakhong.data[0] ?? null}
          damTier={lamTakhong.fallbackTier === "loading" ? null : lamTakhong.fallbackTier}
          damAge={lamTakhong.ageMinutes}
          weather={weather.data[0] ?? null}
          air={air4thai.data[0] ?? airQuality.data[0] ?? null}
          drought={drought.data[0] ?? null}
          incidents={[...cityReports.data, ...iticEvents.data]
            .filter((i) => i.status !== "resolved")
            .slice(0, 30)}
          onOpenFullMap={toggleBrief}
          onOpenChat={openChat}
          onOpenManual={openManual}
          updatedAt={new Date()}
        />
      )}

      {/* Feed-failure banner. Deliberately quiet: see lib/systemBanner.ts for
          when this is allowed to interrupt the Mayor, and why an unconfigured
          optional key must never do so. */}
      {bannerVerdict && (
        <div className={`system-banner banner-${bannerVerdict.severity}`}>
          <span className="mono">
            {bannerMessage(bannerVerdict)}
            {" · "}
            <a href={`${API_BASE}/api/health/detailed`} target="_blank" rel="noreferrer" className="banner-link">
              View details <Arrow direction="up-right" />
            </a>
          </span>
        </div>
      )}
      {!systemHealth && systemHealthError && (
        <div className="system-banner banner-down">
          <span className="mono">
            API HOST UNREACHABLE — {API_BASE.replace(/^https?:\/\//, "")} · {systemHealthError}{" · "}
            <button type="button" className="banner-link banner-retry" onClick={refetchSystemHealth}>
              Retry <Arrow direction="up-right" />
            </button>
          </span>
        </div>
      )}


      {/* ── Left sidebar: Sikhio municipal brief.
          EXEC is the Mayor's default — water hero + live executive brief.
          No welcome overlay: land on the data. OPS shares the same rail. ── */}
      <aside
        id="main-content"
        tabIndex={-1}
        className="left-bar"
        aria-hidden={isMobile && mobilePanel !== "brief"}
      >
        {/* Page-level headings for screen readers. Visible focus target
            for the skip link; nothing on the page should be more important
            than this heading for assistive tech navigation. */}
        <h1 className="sr-only">Sikhio Town Municipality Digital Twin</h1>
        <h2 className="sr-only">
          แดชบอร์ดเมืองสีคิ้ว · น้ำ-ภัยแล้ง-คุณภาพอากาศ-จราจร-เหตุการณ์
        </h2>
        {/* Water security is the headline mission of this dashboard.
            The hero card pins Lam Takhong + Drought + Air at the top of
            the left rail, regardless of lens — the Mayor should see
            the water situation first, every time. */}
        <div className="left-section water-hero-section">
          <WaterSecurityHero
            dam={lamTakhong.data[0] ?? null}
            damAgeMinutes={lamTakhong.ageMinutes}
            damTier={lamTakhong.fallbackTier === "loading" ? undefined : lamTakhong.fallbackTier}
            drought={drought.data[0] ?? null}
            droughtAgeMinutes={drought.ageMinutes}
            droughtTier={drought.fallbackTier === "loading" ? undefined : drought.fallbackTier}
            pm25={airQuality.data[0]?.pm25 ?? air4thai.data[0]?.pm25 ?? null}
            riverLevelForecast={
              forecastMetrics.find((m) => m.metric === "riverLevel.forecast")?.horizon ?? null
            }
            onViewOnMap={() => {
              onLensChange("environment");
              flyTo(101.5617, 14.865, 13.5);
            }}
          />
        </div>
        {lens === "intelligence" && (
          <div className="left-section" style={{ paddingBottom: 12 }}>
            <Suspense fallback={null}>
              <SituationDigest
                nasaReadings={nasaEarth.data[0] ?? null}
                forecastAlerts={forecastAlerts}
                forecasts={forecastMetrics}
              />
            </Suspense>
          </div>
        )}
        {lens === "executive" && (
          <CollapsibleSection id="exec-brief" title="วันนี้ · TODAY" defaultOpen>
            <MemoExecutiveBriefing
              executive={executive.data[0] ?? null}
              weather={weather.data[0] ?? null}
              airQuality={airQuality.data[0] ?? air4thai.data[0] ?? null}
              openIncidents={cityReports.data.filter((r) => r.status !== "resolved").length + iticEvents.data.length}
              reportsLive={cityReports.fallbackTier === "live" || iticEvents.fallbackTier === "live"}
              dam={lamTakhong.data[0] ?? null}
              reservoirs={reservoirs.data}
              adapterHealth={systemHealth?.adapters}
              ageMinutes={executive.ageMinutes}
              fallbackTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
              waterGaps={[
                ...(!lamTakhong.data[0] && lamTakhong.fallbackTier !== "loading" ? ["DAM"] : []),
                ...(!drought.data[0] && drought.fallbackTier !== "loading" ? ["DRGT"] : []),
                ...(!floodDash.data[0]?.risk && floodDash.fallbackTier !== "loading" ? ["FLD"] : []),
                ...(basinWater.data.filter((s) => s.kind === "waterlevel").length === 0
                  && basinWater.fallbackTier !== "loading" ? ["BASIN"] : []),
              ]}
            />
          </CollapsibleSection>
        )}
        {provincialKPIs.data.length > 0 && (
          <CollapsibleSection id="province-kpis" title="จังหวัด · PROVINCE" defaultOpen={false}>
            <ProvincialKPIs
              data={provincialKPIs.data[0] ?? null}
              loading={provincialKPIs.fallbackTier === "loading"}
              ageMinutes={provincialKPIs.ageMinutes}
              fallbackTier={provincialKPIs.fallbackTier === "loading" ? undefined : provincialKPIs.fallbackTier}
            />
          </CollapsibleSection>
        )}
        {/* Detail dam/drought panels — water-focused lenses + EXEC (FloodDash is
            province attention; dam numbers already live in the hero). */}
        {(showWatershed) && (
          <CollapsibleSection
            id="water-detail"
            title={lens === "executive" ? "ลุ่มน้ำ · WATERSHED" : "น้ำละเอียด · WATER DETAIL"}
            /* MSH: measured 1074px expanded in the default EXEC lens, against a
               764px rail. The dam figure the operator came for is already the
               rail's hero at the top; opening the detail by default stacked a
               second presentation of the same numbers underneath it and
               pushed everything else off the first screen. */
            defaultOpen={false}
          >
            {showWaterDetail && (
              <>
                <FloodRiskPanel
                  data={lamTakhong.data[0] ?? null}
                  loading={lamTakhong.fallbackTier === "loading"}
                  ageMinutes={lamTakhong.ageMinutes}
                  fallbackTier={lamTakhong.fallbackTier === "loading" ? undefined : lamTakhong.fallbackTier}
                />
                <DroughtPanel
                  data={drought.data[0] ?? null}
                  loading={drought.fallbackTier === "loading"}
                  ageMinutes={drought.ageMinutes}
                  fallbackTier={drought.fallbackTier === "loading" ? undefined : drought.fallbackTier}
                />
              </>
            )}
            <BasinGaugesStrip
              stations={basinWater.data}
              loading={basinWater.fallbackTier === "loading"}
              ageMinutes={basinWater.ageMinutes}
              fallbackTier={basinWater.fallbackTier === "loading" ? undefined : basinWater.fallbackTier}
            />
            <FloodDashPanel
              data={floodDash.data[0] ?? null}
              loading={floodDash.fallbackTier === "loading"}
              ageMinutes={floodDash.ageMinutes}
              fallbackTier={floodDash.fallbackTier === "loading" ? undefined : floodDash.fallbackTier}
            />
          </CollapsibleSection>
        )}
        <CollapsibleSection
          id="social-listening"
          title="เสียงเมือง · SOCIAL"
          /* MSH: measured 1714px expanded — 38% of everything in the left
             rail, and the single largest thing in it. The section is the
             rail's longest read, not its most important one: the dam figure
             the operator came for is the hero at the top of the rail, and
             the actionable queue lives in the right bar. Collapsed, this
             heading is one line and the feed is one click away; expanded by
             default it put 1,700px between the operator and the panels they
             actually came for. */
          defaultOpen={false}
        >
          <SocialListeningPanel
            news={news.data}
            facebook={facebook.data}
            loading={news.fallbackTier === "loading"}
            ageMinutes={news.ageMinutes}
            fallbackTier={news.fallbackTier === "loading" ? undefined : news.fallbackTier}
            facebookTier={facebook.fallbackTier === "loading" ? undefined : facebook.fallbackTier}
          />
        </CollapsibleSection>
        <CollapsibleSection id="air-health" title="อากาศ · AIR" defaultOpen={false}>
          <AqiBadge
            trend={aqiTrend.data[0] ?? null}
            loading={aqiTrend.fallbackTier === "loading"}
            livePoint={airQuality.data[0] ?? air4thai.data[0] ?? null}
          />
          <AirDashPanel
            data={airDash.data[0] ?? null}
            loading={airDash.fallbackTier === "loading"}
            ageMinutes={airDash.ageMinutes}
            fallbackTier={airDash.fallbackTier === "loading" ? undefined : airDash.fallbackTier}
          />
        </CollapsibleSection>
        <CollapsibleSection id="town-services" title="บริการเมือง · SERVICES" defaultOpen={false}>
        <div className="left-section left-section-divided">
          <HealthPanel
            data={mophHealth.data}
            loading={mophHealth.fallbackTier === "loading"}
            ageMinutes={mophHealth.ageMinutes}
            fallbackTier={mophHealth.fallbackTier === "loading" ? undefined : mophHealth.fallbackTier}
            municipalPopulation={muniProfile.data[0]?.population?.total ?? null}
          />
        </div>
        <div className="left-section left-section-divided">
          <MunicipalProfilePanel
            data={muniProfile.data[0] ?? null}
            loading={muniProfile.fallbackTier === "loading"}
            ageMinutes={muniProfile.ageMinutes}
            fallbackTier={muniProfile.fallbackTier === "loading" ? undefined : muniProfile.fallbackTier}
          />
        </div>
        {/* Reference-seed panels (no live municipal API yet) stay off EXEC —
            the Mayor's landing brief is live feeds only. Drill-down lenses
            still show them with an honest REFERENCE tier badge. */}
        {lens !== "executive" && (
          <>
        <div className="left-section left-section-divided">
          <AgriPanel
            data={agriculture.data}
            loading={agriculture.fallbackTier === "loading"}
            ageMinutes={agriculture.ageMinutes}
            fallbackTier={agriculture.fallbackTier === "loading" ? undefined : agriculture.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <KhaoYaiPanel
            data={khaoYai.data[0] ?? null}
            loading={khaoYai.fallbackTier === "loading"}
            ageMinutes={khaoYai.ageMinutes}
            fallbackTier={khaoYai.fallbackTier === "loading" ? undefined : khaoYai.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <RailPanel
            data={railway.data}
            loading={railway.fallbackTier === "loading"}
            ageMinutes={railway.ageMinutes}
            fallbackTier={railway.fallbackTier === "loading" ? undefined : railway.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <WastePanel
            data={waste.data[0] ?? null}
            loading={waste.fallbackTier === "loading"}
            ageMinutes={waste.ageMinutes}
            fallbackTier={waste.fallbackTier === "loading" ? undefined : waste.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <ElderCarePanel
            data={elderCare.data[0] ?? null}
            loading={elderCare.fallbackTier === "loading"}
            ageMinutes={elderCare.ageMinutes}
            fallbackTier={elderCare.fallbackTier === "loading" ? undefined : elderCare.fallbackTier}
          />
        </div>
          </>
        )}
        <div className="left-section left-section-divided">
          <HighwayPanel
            data={highway.data[0] ?? null}
            loading={highway.fallbackTier === "loading"}
            ageMinutes={highway.ageMinutes}
            fallbackTier={highway.fallbackTier === "loading" ? undefined : highway.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <LocalMarketPanel
            data={localMarket.data[0] ?? null}
            loading={localMarket.fallbackTier === "loading"}
            ageMinutes={localMarket.ageMinutes}
            fallbackTier={localMarket.fallbackTier === "loading" ? undefined : localMarket.fallbackTier}
          />
        </div>
        </CollapsibleSection>
        <CollapsibleSection id="earth-forecast" title="โลก + พยากรณ์ · EARTH" defaultOpen={false}>
        <div className="left-section left-section-divided">
          <EarthAlphaBrief
            enabledLayers={enabledLayers}
            onToggleLayer={onToggleLayer}
            floodZoneCount={floodRisk?.features.length ?? 0}
            waterwayCount={waterways?.features.length ?? 0}
            openIncidentCount={cityReports.data.filter((r) => r.status !== "resolved").length + iticEvents.data.length}
            sheetsConfigured={sheetsConfigured}
            nasaReadings={nasaEarth.data[0] ?? null}
            ageMinutes={nasaEarth.ageMinutes}
            fallbackTier={nasaEarth.fallbackTier === "loading" ? undefined : nasaEarth.fallbackTier}
          />
        </div>
        {reservoirs.data.length > 0 && (
          <div className="left-section left-section-divided">
            <WaterPanel
              data={reservoirs.data}
              loading={reservoirs.fallbackTier === "loading"}
              ageMinutes={reservoirs.ageMinutes}
            />
          </div>
        )}
        <div className="left-section left-section-divided">
          <Suspense fallback={<div className="col"><div className="eyebrow">FORECAST · พยากรณ์</div><div className="caption">loading…</div></div>}>
            <PredictivePanelLazy            apiBase={API_BASE}
              onMetricClick={handleForecastMetricClick}
              onAlert={handleForecastAlert}
              onForecastsLoaded={(f) => setForecastMetrics(f)}
            />
          </Suspense>
        </div>
        </CollapsibleSection>
        {/* Modelled traffic + device tools are staff surfaces — keep them
            off the Mayor's EXEC briefing so the rail stays about water,
            people, and decisions. */}
        {lens !== "executive" && (
          <MemoPmcuBrief
            hour={hour}
            isWeekend={isWeekend}
            iticEvents={iticEvents.data}
            cityReports={cityReports.data}
            trafficSampleCount={trafficSamples.length}
          />
        )}
        <div className="left-section">
          {/* No wrapper label here — KpiStrip renders its own PanelHeader with
              the same title, and two identical headings stacked read as a bug. */}
          <MemoKpiStrip
            cityReports={cityReports.data}
            iticEvents={iticEvents.data}
            airQuality={airQuality.data.length > 0 ? airQuality.data : air4thai.data}
            weather={weather.data}
            reportsTier={cityReports.fallbackTier}
            iticTier={iticEvents.fallbackTier}
            aqTier={airQuality.data.length > 0 ? airQuality.fallbackTier : air4thai.fallbackTier}
            weatherTier={weather.fallbackTier}
          />
        </div>
      </aside>

      {/* ── Map center — nothing overlaps this ── */}
      <main className="map-area" aria-hidden={isMobile && mobilePanel !== "map"}>
        <div className="map-host">
          <div className="sr-only" role="status" aria-live="polite">
            Sikhio map. {mapViewState.kind === "custom" ? mapViewState.label : `Current lens: ${LENSES.find((l) => l.id === lens)?.label ?? lens}`}.
            {enabledLayers.size} layers are currently enabled. Use Clean aerial view to inspect satellite imagery without data overlays.
          </div>
          <DeckGL
            viewState={viewState}
            onViewStateChange={handleViewStateChange}
            // Controller options — minZoom/maxZoom live in MapStateProps which is not
            // re-exported by @deck.gl/core, so we extend ControllerProps locally.
            // All three constraints enforce bounds *before* onViewStateChange fires,
            // preventing the one-frame "flash to outer space" that a pure React clamp cannot.
            // scrollZoom.speed: default 0.01 <Arrow direction="right" /> one fast trackpad swipe jumps 5+ levels;
            // 0.004 is ~2.5× slower, still comfortable but safe.
            controller={{ minZoom: 11, maxZoom: 20, maxBounds: SIKHIO.outerBounds, scrollZoom: { speed: 0.004, smooth: false } } as ControllerProps & { minZoom?: number; maxZoom?: number }}
            layers={layers}
            getTooltip={tooltipForPickMemo}
            onClick={handleMapClick}
            onHover={(info) => {
              const c = info.coordinate;
              if (!c || c.length < 2) return;
              if (cursorRaf.current != null) cancelAnimationFrame(cursorRaf.current);
              cursorRaf.current = requestAnimationFrame(() => {
                setCursorLngLat({ lng: c[0], lat: c[1] });
              });
            }}
          >
            <MapLibreMap
              mapStyle={mapStyle}
              mapLib={maplibregl as unknown as typeof maplibregl}
              attributionControl={false}
              renderWorldCopies={false}
            >
              {/* GIBS satellite raster sources — rendered via MapLibre for
                  reliable tile stretching at any viewport zoom. Only active
                  layers are mounted so MapLibre only fetches what's needed.
                  `GIBS_RENDER` carries pre-built, stable {tiles, paint} refs
                  so React-render churn doesn't make MapLibre re-diff every frame. */}
              {GIBS_RENDER.filter(g => enabledLayers.has(g.id as LayerId)).map(g => (
                <Source
                  key={g.id}
                  id={`gibs-src-${g.id}`}
                  type="raster"
                  tiles={g.tiles as unknown as string[]}
                  tileSize={256}
                  minzoom={0}
                  maxzoom={g.maxzoom}
                >
                  <MapLayer
                    id={`gibs-lyr-${g.id}`}
                    type="raster"
                    paint={g.paint}
                    beforeId="labels-top"
                  />
                </Source>
              ))}
              {/* GISTDA disaster raster overlays — proxied TMS (scheme="tms"),
                  key-safe. Mounted only when the layer is enabled. */}
              {GISTDA_TILE_RENDER.filter(g => enabledLayers.has(g.id)).map(g => (
                <Source
                  key={g.id}
                  id={`gistda-src-${g.id}`}
                  type="raster"
                  tiles={g.tiles as unknown as string[]}
                  tileSize={256}
                  minzoom={0}
                  maxzoom={19}
                >
                  <MapLayer
                    id={`gistda-lyr-${g.id}`}
                    type="raster"
                    paint={g.paint}
                    beforeId="labels-top"
                  />
                </Source>
              ))}
              {enabledLayers.has("satellite-esri") && (
                <Source
                  id="esri-world-imagery-src"
                  type="raster"
                  tiles={["https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"]}
                  tileSize={256}
                  maxzoom={19}
                >
                  <MapLayer
                    id="esri-world-imagery"
                    type="raster"
                    paint={{ "raster-opacity": 1 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
              {enabledLayers.has("google-satellite") && googleSatSession && (
                <Source
                  id="google-satellite-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleSatSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-satellite-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 1 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
              {enabledLayers.has("google-traffic") && googleTrafficSession && (
                <Source
                  id="google-traffic-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleTrafficSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-traffic-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 0.85 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
            </MapLibreMap>
          </DeckGL>
          {/* Tab order must follow visual order (WCAG 2.4.3). AERIAL/CLEAR
              sit left of the search box, so they come first in the DOM. */}
          <MapOverlayControls
            mapViewState={mapViewState}
            onAerialOnly={setAerialOnly}
            onClearOverlays={clearOverlays}
            onRestoreLens={restoreLens}
          />
          <BuildingSearch
            buildings={buildings}
            onSelect={(centroid, props) => {
              setSelectedBuilding(props);
              setSelectedCoord([centroid[0], centroid[1]]);
              flyTo(centroid[0], centroid[1], 17.5);
            }}
          />
          {enabledLayers.has("cctv-cameras") && (
            <CctvWall
              apiBase={API_BASE}
              cameras={allCctv}
              centres={cvCentres.data}
              open={cctvWallOpen}
              onOpenChange={setCctvWallOpen}
              onOpenCentre={() => setCvCentreOpen(true)}
              selectedCameraId={selectedCctv?.id}
              onSelect={(cam) => {
                setSelectedBuilding(null);
                setSelectedIncident(null);
                setSelectedCctv(cam);
              }}
            />
          )}
          <MapCursorReadout
            lng={cursorLngLat?.lng ?? null}
            lat={cursorLngLat?.lat ?? null}
          />
          {enabledLayers.has("municipality-buildings") && <BuildingLegend />}
          {/* Map zoom controls — back-up for gesture / trackpad */}
          <div className="zoom-controls" aria-label="ปุ่มซูมแผนที่">
            <button onClick={() => zoomBy(0.6)} aria-label="ขยายเข้า" className="zoom-btn">+</button>
            <button onClick={() => zoomBy(-0.6)} aria-label="ขยายออก" className="zoom-btn">−</button>
          </div>
          {/* Basemap strip — only visible in 2D plan mode */}
          {viewMode === "2D" && (
            <BasemapStrip current={basemapId} onChange={setBasemapId} />
          )}
          <BuildingCard
            building={selectedBuilding}
            coord={selectedCoord}
            onClose={() => setSelectedBuilding(null)}
          />
          <IncidentCard
            incident={selectedIncident}
            onClose={() => setSelectedIncident(null)}
          />
          <CctvLiveModal
            camera={selectedCctv}
            onClose={() => setSelectedCctv(null)}
          />
          {cvCentreOpen && (
            <CctvCentre
              apiBase={API_BASE}
              cameras={allCctv}
              centres={cvCentres.data}
              centresLoading={cvCentres.fallbackTier === "loading"}
              onClose={() => setCvCentreOpen(false)}
              onOpenCamera={(cam) => {
                setCvCentreOpen(false);
                setSelectedBuilding(null);
                setSelectedIncident(null);
                setSelectedCctv(cam);
              }}
            />
          )}
          {forecastAlerts.size > 0 && (
            <div style={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              zIndex: 20, display: "flex", gap: 6, pointerEvents: "none",
            }}>
              {[...forecastAlerts].map((m) => (
                <span key={m} className="mono eyebrow" style={{
                  background: "var(--bad)", color: "var(--bg)",
                  padding: "var(--s-1) var(--s-2)", fontSize: "var(--size-eyebrow)", letterSpacing: "0.1em",
                }}>▲ {METRIC_LABEL[m] ?? m} FORECAST</span>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ── Right sidebar ───────────────────────────────────────────────────
          MSH: the rail used to open with the Mayor's Desk and Google Trends,
          which pushed LAYERS — the control that defines what the entire map
          shows — down to y≈418 of 772, i.e. behind a news feed and a search
          trend nobody had asked for yet. The reading order now follows the
          task: choose the lens, then add layers, then read the brief. News
          and trends keep their content and their own collapsible sections. */}
      <aside className="right-bar" aria-hidden={isMobile && mobilePanel !== "layers"}>
        {/* MSH: LAYERS was expanded on arrival and took 432px of the 764px
            rail — 57% of the instrument. The Mayor's Desk, the primary task,
            was left with 153px, of which the news window was 66px, and with
            the action legend at the top of that window it showed ZERO
            headlines. A mode switch the mayor uses when they want to LOOK at
            something was taking the space the surface they READ lives in.
            Collapsed, the Desk gets 388px and a 301px news window; LAYERS is
            one click away, and CollapsibleSection persists the choice, so an
            operator who works in the palette keeps it open. When they DO
            open it they are working in the tool, and the Desk can wait —
            which is the right trade. The situation itself is also on the
            left rail's brief, so nothing is only here. */}
        {/* MSH: the LENS picker and the LAYER switches are not the same task
            and must not share one disclosure. The lens is the mode — the one
            control that decides what the map shows — so it is permanently
            visible at the top of the rail. The layer switches are
            configuration, used occasionally, and they are 792px of content,
            so they go behind the disclosure.

            They used to share the LAYERS section, and when that section was
            collapsed to give the Mayor's Desk a real reading window, the
            dashboard lost its only lens switcher entirely: measured with the
            section collapsed, `.lens-label-en` count in the DOM = 0.

            Measured at 1440x900: lens block 298px (eyebrow 17 + grid 178 +
            explainer 103) + LAYERS collapsed 37px + Desk 242px + Facebook 55px
            + Trends 132px = 764px. The rail fits, the mode switch is on
            screen, and the Desk still has a real window. */}
        <div className="right-lens">
          <MemoLensPicker lens={lens} onLensChange={onLensChange} />
        </div>
        <CollapsibleSection id="map-layers" title="ชั้นข้อมูล · LAYERS" defaultOpen={false} className="rail-section--tool">
        <div className="right-layers">
          <MemoLayerPalette
            enabled={enabledLayers}
            onToggleLayer={onToggleLayer}
            counts={layerCounts}
            statuses={layerStatuses}
          />
        </div>
        </CollapsibleSection>
        <CollapsibleSection id="mayor-desk" title="โต๊ะนายก · MAYOR'S DESK" defaultOpen className="rail-section--grow">
        <div className="right-news">
          <Suspense fallback={<div className="caption">loading news…</div>}>
            <NewsDeskLazy
              items={news.data}
              loading={news.fallbackTier === "loading"}
              ageMinutes={news.ageMinutes}
              onRefresh={news.refetch}
            />
          </Suspense>
        </div>
        </CollapsibleSection>
        {/* MSH: the Facebook embed was a child of `.right-news` — the Desk's
            own scroll window. Measured at 1440: that window was 66px tall and
            held 2104px of content (1460px of news + 620px of Facebook panel),
            so a 620px panel sat 1570px down inside a 66px box that the reader
            had to scroll twice to reach. The embed is a source, not part of
            the Mayor's action queue, so it is now a rail-level section.
            It is CLOSED by default: expanded, it took 571px of the 764px
            rail and pushed the Mayor's Desk — the primary task — down to a
            66px news window. A collapsed section keeps the feature one click
            away and gives the Desk the first screen it is supposed to have.
            (The panel's own PanelHeader dropped its title so this section
            heading is the only one for the region.) */}
        <CollapsibleSection id="municipal-facebook" title="เพจเทศบาล · MUNICIPAL FACEBOOK" defaultOpen={false}>
          <Suspense fallback={<div className="caption">loading facebook…</div>}>
            <FacebookPanelLazy
              posts={facebook.data}
              ageMinutes={facebook.ageMinutes}
              fallbackTier={facebook.fallbackTier === "loading" ? undefined : facebook.fallbackTier}
            />
          </Suspense>
        </CollapsibleSection>
        {trendsHasSignal && (
          <div className="right-trends">
            <Suspense fallback={<div className="caption">loading trends…</div>}>
              <TrendsPanelLazy
                snapshots={trends.data}
                loading={trends.fallbackTier === "loading"}
                ageMinutes={trends.ageMinutes}
                onRefresh={trends.refetch}
                fallbackTier={trends.fallbackTier === "loading" ? undefined : trends.fallbackTier}
              />
            </Suspense>
          </div>
        )}
      </aside>

      {/* ── Bottom bar: ident / traffic timeline / counts ── */}
      <div className="bottom-bar">
        <div className="bottom-ident">
          <span className="pill">v0.2</span>
          {/* MSH: "Sikhio Town · Nakhon Ratchasima" was clipped out of
              existence by nowrap + overflow:hidden (measured 323px box vs
              501px content at 1440) — and it was the wrong thing to keep.
              The topbar directly above already says "Sikhio Town
              Municipality" and "STM-01 · Nakhon Ratchasima" in larger type.
              Dropping the duplicate gives the line its width back, so the
              DT·L2 standards pill and the UNDP · ADB attribution — the only
              place either appears — are painted instead of clipped. */}
          <span className="pill pill-standard" title="UNDP-JTC Digital Twins for Cities (Jul 2025) · ADB Digital Twin Framework (May 2025)">DT·L2</span>
          <span className="bottom-standard mono">UNDP · ADB</span>
        </div>
        <div className="bottom-mid">
          <button
            type="button"
            className="mono chat-trigger"
            onClick={() => setChatOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={chatOpen}
            aria-label="Open concierge chat — ask the dashboard about Lam Takhong, drought, traffic, air quality"
          >
            <span className="chat-trigger-dot" aria-hidden />
            ถาม STM
            <Arrow direction="right" />
          </button>
          <MemoHourRail          hour={hour}
            isWeekend={isWeekend}
            onHourChange={setHour}
            onWeekendToggle={setIsWeekend}
          />
        </div>
        <div className="bottom-stats">
          <span>{buildings?.features.length ?? 0} BUILDINGS · {trafficSamples.length} ROADS · {layers.length} LAYERS</span>
          <span>{civicPoints?.features.length ?? 0} CIVIC · {allCctv.length} CCTV</span>
        </div>
      </div>

      {/* Modals — lazy-loaded chunks, fetched only on first open */}
      <Suspense fallback={null}>
        {catalogOpen && <SourceCatalog open={catalogOpen} onClose={() => setCatalogOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {manualOpen && <Manual open={manualOpen} onClose={() => setManualOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {whitepaperOpen && <Whitepaper open={whitepaperOpen} onClose={() => setWhitepaperOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {sheetsOpen && (
          <SheetsPanel
            open={sheetsOpen}
            onClose={() => {
              setSheetsOpen(false);
              try {
                setSheetsConfigured(Boolean(localStorage.getItem(SHEETS_STORAGE_KEY)));
              } catch (err) {
                // axiom-audit-ignore-next-line back -- localStorage private mode; setSheetsConfigured(false) is the safe fallback the next state already has.
                if (typeof console !== "undefined") console.warn("[App] sheets refresh failed", err);
              }
            }}
          />
        )}
      </Suspense>
      <Suspense fallback={null}>
        <ChatBox apiBase={API_BASE} open={chatOpen} onOpenChange={setChatOpen} />
      </Suspense>
      {isMobile && <MobileNav panel={mobilePanel} onChange={setMobilePanel} />}
    </div>
  );
}
